<template>
    <main>
        <HeaderTab :title="$t('menu.compte.securite')" />
        <div id="content">
            <div class="container-fluid">
                <div class="row">
                    <div class="col-12 col-lg-6">
                        <div class="box">
                            <h3 class="mb-4">{{ $t('compte.modifier_mdp') }}</h3>

                            <form @submit.prevent="send">
                                <!-- Mot de passe actuel -->
                                <div class="form-group">
                                    <label for="current_password">{{ $t('compte.password_current') }}<sup>*</sup></label>

                                    <div class="input-group">
                                        <input
                                            :type="showPasswords.current_password ? 'text' : 'password'"
                                            class="form-control"
                                            id="current_password"
                                            name="current_password"
                                            :placeholder="$t('compte.password_current')"
                                            v-model="form.current_password"
                                            :disabled="isWorking"
                                            autocomplete="current-password"
                                            required
                                        >
                                        <div class="input-group-append">
                                            <button type="button" class="btn btn-outline-secondary" @click="showPasswords.current_password = !showPasswords.current_password">
                                                <font-awesome-icon :icon="showPasswords.current_password ? ['fal', 'eye-slash'] : ['fal', 'eye']" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Nouveau mot de passe -->
                                <div class="form-group">
                                    <label for="password">{{ $t('compte.password_new') }}<sup>*</sup></label>

                                    <div class="input-group">
                                        <input
                                            :type="showPasswords.password ? 'text' : 'password'"
                                            class="form-control"
                                            id="password"
                                            name="password"
                                            :placeholder="$t('compte.password_new')"
                                            v-model="form.password"
                                            :disabled="isWorking"
                                            autocomplete="new-password"
                                            required
                                        >
                                        <div class="input-group-append">
                                            <button type="button" class="btn btn-outline-secondary" @click="showPasswords.password = !showPasswords.password">
                                                <font-awesome-icon :icon="showPasswords.password ? ['fal', 'eye-slash'] : ['fal', 'eye']" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Confirmation du mot de passe -->
                                <div class="form-group">
                                    <label for="password_confirmation">{{ $t('compte.password_confirmation') }}<sup>*</sup></label>

                                    <div class="input-group">
                                        <input
                                            :type="showPasswords.password_confirmation ? 'text' : 'password'"
                                            class="form-control"
                                            :class="{ 'is-invalid': form.password_confirmation && !passwordsMatch }"
                                            id="password_confirmation"
                                            name="password_confirmation"
                                            :placeholder="$t('compte.password_confirmation')"
                                            v-model="form.password_confirmation"
                                            :disabled="isWorking"
                                            autocomplete="new-password"
                                            required
                                        >
                                        <div class="input-group-append">
                                            <button type="button" class="btn btn-outline-secondary" @click="showPasswords.password_confirmation = !showPasswords.password_confirmation">
                                                <font-awesome-icon :icon="showPasswords.password_confirmation ? ['fal', 'eye-slash'] : ['fal', 'eye']" />
                                            </button>
                                        </div>
                                        <div class="invalid-feedback">{{ $t('error.IPWC') }}</div>
                                    </div>
                                </div>

                                <!-- Règles du mot de passe -->
                                <div>
                                    {{ $t('compte.mdp_doit_contenir') }} :
                                    <ul>
                                        <li :class="rules.lettre ? 'password_green' : 'password_red'">{{ $t('compte.mdp_au_moins_une_lettre') }}</li>
                                        <li :class="rules.chiffre ? 'password_green' : 'password_red'">{{ $t('compte.mdp_au_moins_un_chiffre') }}</li>
                                        <li :class="rules.symbole ? 'password_green' : 'password_red'">{{ $t('compte.mdp_au_moins_symbole') }}</li>
                                        <li :class="rules.caracteres ? 'password_green' : 'password_red'">{{ $t('compte.mdp_plus_12_caractere') }}</li>
                                    </ul>
                                </div>

                                <b-alert v-if="errorInfos" variant="danger" show>
                                    {{ errorInfos }}
                                </b-alert>

                                <div class="mt-4">
                                    <b-button type="submit" variant="primary" class="rounded-pill px-5" :disabled="!canSubmit">
                                        <font-awesome-icon v-if="isWorking" class="mr-1" :icon="['fas', 'spinner']" pulse />
                                        {{ $t('global.enregistrer') }}
                                    </b-button>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="col-12 col-lg-6">
                        
                        <div class="box">
                            <h3 class="mb-4">{{ $t('compte.two_factor.title') }}</h3>
                            <p>{{ $t('compte.two_factor.description') }}</p>

                            <div v-if="twoFactor.loading" class="text-center">
                                <font-awesome-icon :icon="['fas', 'spinner']" pulse />
                            </div>

                            <!-- Activée -->
                            <p v-else-if="twoFactor.enabled" class="text-success mb-0">
                                <font-awesome-icon class="mr-1" :icon="['far', 'check-circle']" />
                                {{ $t('compte.two_factor.enabled') }}
                            </p>

                            <!-- Activation en cours : QR code à scanner, puis confirmation par un code -->
                            <form v-else-if="twoFactor.setup" @submit.prevent="enableTwoFactorAuth">
                                <p>{{ $t('compte.two_factor.setup_scan') }}</p>
                                <div class="text-center mb-3">
                                    <img :src="twoFactor.setup.qr" alt="QR code" width="200" height="200" class="bg-white p-2">
                                </div>
                                <p class="small">
                                    {{ $t('compte.two_factor.setup_manual') }}
                                    <code>{{ twoFactor.setup.secret }}</code>
                                </p>

                                <div class="form-group">
                                    <label for="two_factor_code">{{ $t('compte.two_factor.setup_code') }}<sup>*</sup></label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="two_factor_code"
                                        name="two_factor_code"
                                        v-model="twoFactor.code"
                                        inputmode="numeric"
                                        autocomplete="one-time-code"
                                        maxlength="7"
                                        :disabled="twoFactor.working"
                                        required
                                    >
                                </div>

                                <div class="form-group">
                                    <label for="two_factor_password">{{ $t('compte.password_current') }}<sup>*</sup></label>
                                    <input
                                        type="password"
                                        class="form-control"
                                        id="two_factor_password"
                                        name="two_factor_password"
                                        v-model="twoFactor.password"
                                        autocomplete="current-password"
                                        :disabled="twoFactor.working"
                                        required
                                    >
                                </div>

                                <b-alert v-if="twoFactor.error" variant="danger" show>
                                    {{ twoFactor.error }}
                                </b-alert>

                                <div class="mt-4">
                                    <b-button variant="secondary" class="rounded-pill mr-2" :disabled="twoFactor.working" @click="cancelTwoFactorSetup">
                                        {{ $t('global.annuler') }}
                                    </b-button>
                                    <b-button type="submit" variant="primary" class="rounded-pill px-5" :disabled="!canEnableTwoFactor">
                                        <font-awesome-icon v-if="twoFactor.working" class="mr-1" :icon="['fas', 'spinner']" pulse />
                                        {{ $t('compte.two_factor.confirm') }}
                                    </b-button>
                                </div>
                            </form>

                            <!-- Désactivée -->
                            <div v-else>
                                <b-alert v-if="twoFactor.error" variant="danger" show>
                                    {{ twoFactor.error }}
                                </b-alert>

                                <b-button variant="primary" class="rounded-pill px-5" :disabled="twoFactor.working" @click="startTwoFactorSetup">
                                    <font-awesome-icon v-if="twoFactor.working" class="mr-1" :icon="['fas', 'spinner']" pulse />
                                    {{ $t('compte.two_factor.activate') }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import User from '@/mixins/User'

export default {
    name: 'Security',
    mixins: [User],
    data: () => ({
        form: {
            current_password: '',
            password: '',
            password_confirmation: ''
        },
        isWorking: false,
        // Un état par champ : chaque bouton n'affiche que son propre mot de passe
        showPasswords: {
            current_password: false,
            password: false,
            password_confirmation: false
        },
        errorInfos: null,
        twoFactor: {
            loading: true,
            enabled: false,
            setup: null,
            code: '',
            password: '',
            working: false,
            error: null
        }
    }),
    computed: {
        // Mêmes règles que la création de compte
        rules() {
            const password = this.form.password || ''

            return {
                lettre: /[a-zA-Z]/.test(password),
                chiffre: /\d/.test(password),
                symbole: /[!"#$%&'()*+,-.:;<=>?@[\]^_`{|}~]/.test(password),
                caracteres: password.length >= 12
            }
        },
        passwordsMatch() {
            return this.form.password === this.form.password_confirmation
        },
        canEnableTwoFactor() {
            return !this.twoFactor.working
                && !!this.twoFactor.code
                && !!this.twoFactor.password
        },
        canSubmit() {
            return !this.isWorking
                && !!this.form.current_password
                && Object.values(this.rules).every(Boolean)
                && this.passwordsMatch
        }
    },
    mounted() {
        this.loadTwoFactor()
    },
    methods: {
        async loadTwoFactor() {
            try {
                const status = await this.getTwoFactorStatus()
                this.twoFactor.enabled = status.enabled
            } catch (err) {
                this.twoFactor.error = err.message
            } finally {
                this.twoFactor.loading = false
            }
        },
        // Génère le secret et affiche le QR code : la double authentification n'est active qu'une fois le code confirmé
        async startTwoFactorSetup() {
            this.twoFactor.error = null
            this.twoFactor.working = true

            try {
                this.twoFactor.setup = await this.setupTwoFactor()
            } catch (err) {
                this.twoFactor.error = err.message
            } finally {
                this.twoFactor.working = false
            }
        },
        async enableTwoFactorAuth() {
            if(!this.canEnableTwoFactor) return

            this.twoFactor.error = null
            this.twoFactor.working = true

            try {
                await this.enableTwoFactor(this.twoFactor.password, this.twoFactor.code)

                this.twoFactor.enabled = true
                this.twoFactor.setup = null
                this.successToast('compte.two_factor.activated')
            } catch (err) {
                this.twoFactor.error = err.message
            } finally {
                this.twoFactor.working = false
                this.twoFactor.code = ''
                this.twoFactor.password = ''
            }
        },
        cancelTwoFactorSetup() {
            this.twoFactor.setup = null
            this.twoFactor.code = ''
            this.twoFactor.password = ''
            this.twoFactor.error = null
        },
        async send() {
            if(!this.canSubmit) return

            this.errorInfos = null
            this.isWorking = true

            try {
                await this.changePassword(this.form.current_password, this.form.password, this.form.password_confirmation)

                this.successToast('compte.toast.password_changed')
                this.form = {
                    current_password: '',
                    password: '',
                    password_confirmation: ''
                }
                this.showPasswords = {
                    current_password: false,
                    password: false,
                    password_confirmation: false
                }
            } catch (err) {
                this.errorInfos = err.message
            } finally {
                this.isWorking = false
            }
        }
    },
    components: {
        HeaderTab: () => import('@/components/HeaderTab')
    }
}
</script>
